<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import { getOfferKindLabel } from 'dashboard/helper/lais/offerPresenter';
import WhatsAppCard from './WhatsAppCard.vue';
import ChatWithCard from './ChatWithCard.vue';
import HouseWithResident from './HouseWithResident.vue';
import { formatDate } from '../utils/offerFormatters';
import {
  buyerLabel,
  isOccupiedOffer,
  isRentalOffer,
  offerActivityRows,
  offerPaymentLabel,
  offerStatusDetail,
  offerStatusDetailClass,
  offerSummary,
  rentalOfferPresentationRows,
  whatsappHrefForOffer,
} from '../utils/offerReadModel';

const props = defineProps({
  selectedOffer: { type: Object, default: null },
});

const emit = defineEmits(['chat']);

const kindLabel = kind => getOfferKindLabel(kind);

const selectedOfferActivityRows = computed(() =>
  offerActivityRows(props.selectedOffer)
);

const openClientChat = offer => emit('chat', offer);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="offer-details-presentation min-w-0 rounded-lais border border-lais-border bg-white p-4 sm:p-5"
  >
    <p
      class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
    >
      Contexto comercial
    </p>
    <h3
      class="mt-1 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
    >
      {{ offerSummary(selectedOffer).presentation_title || 'Apresentação' }}
    </h3>
    <dl class="mt-4 grid gap-2.5 text-[13px]">
      <div
        class="offer-details-presentation__row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
      >
        <dt class="text-lais-text-muted">Cliente</dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ buyerLabel(selectedOffer) }}
        </dd>
      </div>
      <div
        class="offer-details-presentation__row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
      >
        <dt class="text-lais-text-muted">Tipo</dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ kindLabel(selectedOffer.kind) }}
        </dd>
      </div>
      <div
        class="offer-details-presentation__row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
      >
        <dt class="text-lais-text-muted">
          {{ isRentalOffer(selectedOffer) ? 'Apresentação' : 'Pagamento' }}
        </dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ offerPaymentLabel(selectedOffer) }}
        </dd>
      </div>
      <div
        v-for="row in rentalOfferPresentationRows(selectedOffer)"
        :key="row.label"
        class="offer-details-presentation__row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
      >
        <dt class="text-lais-text-muted">{{ row.label }}</dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ row.value }}
        </dd>
      </div>
      <div
        v-if="offerStatusDetail(selectedOffer)"
        class="offer-details-presentation__row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
      >
        <dt class="text-lais-text-muted">Status</dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold"
          :class="offerStatusDetailClass(selectedOffer)"
        >
          {{ offerStatusDetail(selectedOffer) }}
        </dd>
      </div>
      <div class="offer-details-presentation__row grid min-w-0 gap-1.5">
        <dt class="text-lais-text-muted">Envio</dt>
        <dd
          class="offer-details-presentation__value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ formatDate(selectedOffer.submitted_at) }}
        </dd>
      </div>
    </dl>

    <div
      v-if="selectedOfferActivityRows.length"
      class="mt-5 border-t border-lais-border-soft pt-5"
    >
      <p
        class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
      >
        Histórico
      </p>
      <h3
        class="mt-1 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        Atividade recente
      </h3>
      <dl class="mt-4 grid gap-2.5 text-[13px]">
        <div
          v-for="row in selectedOfferActivityRows"
          :key="row.label"
          class="offer-details-presentation__activity-row grid min-w-0 gap-1.5 border-b border-lais-border-soft pb-2.5 last:border-0 last:pb-0"
        >
          <dt class="text-lais-text-muted">{{ row.label }}</dt>
          <dd
            class="offer-details-presentation__activity-value min-w-0 break-words font-semibold text-lais-text"
          >
            {{ row.value }}
          </dd>
        </div>
      </dl>
    </div>

    <nav
      aria-label="Atalhos de contato"
      class="mt-5 grid gap-2.5 border-t border-lais-border-soft pt-5"
    >
      <div>
        <p
          class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
        >
          Ações rápidas
        </p>
        <h3
          class="mt-1 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          Atalhos de contato
        </h3>
      </div>
      <WhatsAppCard :href="whatsappHrefForOffer(selectedOffer)" />
      <ChatWithCard
        :label="`Conversar com ${buyerLabel(selectedOffer)}`"
        @open="openClientChat(selectedOffer)"
      />
      <HouseWithResident :show="isOccupiedOffer(selectedOffer)" />
    </nav>
  </section>
</template>

<style scoped>
.offer-details-presentation {
  container-type: inline-size;
}

@container (min-width: 26rem) {
  .offer-details-presentation__row {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    align-items: start;
    gap: 0.75rem;
  }

  .offer-details-presentation__value {
    text-align: right;
  }

  .offer-details-presentation__activity-row {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    align-items: start;
    gap: 0.75rem;
  }

  .offer-details-presentation__activity-value {
    text-align: right;
  }
}
</style>
